import {
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  DialogActions,
  Button,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import omit from "lodash-es/omit";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import {
  CreateS3TenantExportConfigInput,
  CreateS3TenantExportConfigMutation,
  CreateS3TenantExportConfigMutationVariables,
  DeleteS3TenantExportConfigMutation,
  DeleteS3TenantExportConfigMutationVariables,
  GetTenantExportConfigsQuery,
  TrackedJobStatus,
  UpdateS3TenantExportConfigInput,
  UpdateS3TenantExportConfigMutation,
  UpdateS3TenantExportConfigMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import {
  CREATE_S3_TENANT_EXPORT_CONFIG,
  DELETE_S3_TENANT_EXPORT_CONFIG,
  UPDATE_S3_TENANT_EXPORT_CONFIG,
} from "~frontend/graphql/tenantExportConfigs";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { Exact } from "~frontend/util/types";
import useMutation from "~frontend/util/useMutation";

type ExportConfig =
  GetTenantExportConfigsQuery["tenantExportConfigs"]["s3ExportConfig"];

type FormValues = {
  [submitErrorField]: string | null;
  bucket: string;
  prefix: string;
  awsRegion: string;
  awsAccessKeyId: string;
  awsSecretAccessKey: string;
  incremental: "true" | "false";
};

const AWS_REGION_OPTIONS = [
  { label: "us-east-1 - US East (N. Virginia)", value: "us-east-1" },
  { label: "us-east-2 - US East (Ohio)", value: "us-east-2" },
  { label: "us-west-1 - US West (N. California)", value: "us-west-1" },
  { label: "us-west-2 - US West (Oregon)", value: "us-west-2" },
  { label: "af-south-1 - Africa (Cape Town)", value: "af-south-1" },
  { label: "ap-east-1 - Asia Pacific (Hong Kong)", value: "ap-east-1" },
  { label: "ap-south-2 - Asia Pacific (Hyderabad)", value: "ap-south-2" },
  { label: "ap-southeast-3 - Asia Pacific (Jakarta)", value: "ap-southeast-3" },
  {
    label: "ap-southeast-4 - Asia Pacific (Melbourne)",
    value: "ap-southeast-4",
  },
  { label: "ap-south-1 - Asia Pacific (Mumbai)", value: "ap-south-1" },
  { label: "ap-northeast-3 - Asia Pacific (Osaka)", value: "ap-northeast-3" },
  { label: "ap-northeast-2 - Asia Pacific (Seoul)", value: "ap-northeast-2" },
  {
    label: "ap-southeast-1 - Asia Pacific (Singapore)",
    value: "ap-southeast-1",
  },
  { label: "ap-southeast-2 - Asia Pacific (Sydney)", value: "ap-southeast-2" },
  { label: "ap-northeast-1 - Asia Pacific (Tokyo)", value: "ap-northeast-1" },
  { label: "ca-central-1 - Canada (Central)", value: "ca-central-1" },
  { label: "eu-central-1 - Europe (Frankfurt)", value: "eu-central-1" },
  { label: "eu-west-1 - Europe (Ireland)", value: "eu-west-1" },
  { label: "eu-west-2 - Europe (London)", value: "eu-west-2" },
  { label: "eu-south-1 - Europe (Milan)", value: "eu-south-1" },
  { label: "eu-west-3 - Europe (Paris)", value: "eu-west-3" },
  { label: "eu-south-2 - Europe (Spain)", value: "eu-south-2" },
  { label: "eu-north-1 - Europe (Stockholm)", value: "eu-north-1" },
  { label: "eu-central-2 - Europe (Zurich)", value: "eu-central-2" },
  { label: "il-central-1 - Israel (Tel Aviv)", value: "il-central-1" },
  { label: "me-south-1 - Middle East (Bahrain)", value: "me-south-1" },
  { label: "me-central-1 - Middle East (UAE)", value: "me-central-1" },
  { label: "sa-east-1 - South America (São Paulo)", value: "sa-east-1" },
];

function S3ExportConfigDialog({
  onClose,
  exportConfig,
  handleSubmitCreate,
  handleSubmitUpdate,
}: {
  onClose: () => void;
  exportConfig: ExportConfig;
  handleSubmitCreate: <T>(
    settings: Exact<T, CreateS3TenantExportConfigInput>
  ) => Promise<void>;
  handleSubmitUpdate: <T>(
    settings: Exact<T, UpdateS3TenantExportConfigInput>
  ) => Promise<void>;
}) {
  const nonincrementalRowLimit = useFeatureFlag("s3NonincrementalRowLimit");
  const incrementalWindowDays = useFeatureFlag("s3IncrementalWindowDays");

  const isUpdate = !!exportConfig;

  const schema = yup.object().shape({
    bucket: yup.string().required("Required"),
    prefix: yup.string(),
    awsRegion: yup
      .string()
      .required("Required")
      .oneOf(AWS_REGION_OPTIONS.map(o => o.value)),
    awsAccessKeyId: yup.string().required("Required"),
    awsSecretAccessKey: isUpdate
      ? yup.string()
      : yup.string().required("Required"),
  });

  const initialValues: FormValues = {
    [submitErrorField]: null,
    bucket: exportConfig?.settings.bucket ?? "",
    prefix: exportConfig?.settings.prefix ?? "",
    awsRegion: exportConfig?.settings.awsRegion ?? "us-east-1",
    awsAccessKeyId: exportConfig?.settings.awsAccessKeyId ?? "",
    awsSecretAccessKey: "",
    incremental: exportConfig?.settings.incremental ? "true" : "false",
  };

  const handleSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      if (isUpdate) {
        await handleSubmitUpdate({
          ...omit(values, [submitErrorField]),
          incremental: values.incremental === "true",
          awsSecretAccessKey:
            values.awsSecretAccessKey.trim().length > 0
              ? values.awsSecretAccessKey
              : undefined,
        });
      } else {
        await handleSubmitCreate({
          ...omit(values, [submitErrorField]),
          incremental: values.incremental === "true",
        });
      }
    } catch (e: any) {
      formik.setErrors({
        [submitErrorField]: e.message,
      });
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>
        {isUpdate ? "Edit S3 Export Configuration" : "Configure S3 Export"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner noScrollOnError />
              <FormikTextField fullWidth name="bucket" label="S3 Bucket" />
              <FormikTextField
                fullWidth
                name="prefix"
                label="S3 Object Prefix (optional)"
              />
              <FormikSelect fullWidth name="awsRegion" label="AWS Region">
                {AWS_REGION_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </FormikSelect>
              <FormikTextField
                fullWidth
                name="awsAccessKeyId"
                label="AWS Access Key ID"
              />
              <FormikTextField
                fullWidth
                name="awsSecretAccessKey"
                type="password"
                label={
                  isUpdate
                    ? "AWS Secret Access Key (leave blank to keep current)"
                    : "AWS Secret Access Key"
                }
              />
              <FormikSelect
                fullWidth
                name="incremental"
                label="Advanced: Incremental Sync"
              >
                <MenuItem value="false">
                  Disabled (limit {nonincrementalRowLimit.toLocaleString()} rows
                  per table)
                </MenuItem>
                <MenuItem value="true">
                  Enabled (no row limit; only rows updated in the past{" "}
                  {incrementalWindowDays} days will be exported)
                </MenuItem>
              </FormikSelect>
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function S3ExportDeleteButton({
  onDelete,
}: {
  onDelete: () => Promise<any>;
}): ReactElement {
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  return (
    <Button
      disabled={loading}
      color="secondary"
      onClick={async () => {
        const { confirmed } = await confirm({
          title: "Disable S3 Export?",
          description:
            "Are you sure you want to disable S3 exports? Any data in S3 will remain there, but no new exports will be run. You'll need to re-enter your AWS credentials to re-enable exports.",
        });
        if (!confirmed) {
          return;
        }

        setLoading(true);
        await onDelete();
        setLoading(false);
      }}
    >
      {loading ? "Disabling..." : "Disable"}
    </Button>
  );
}

export default function S3TenantExportConfig({
  exportConfig,
}: {
  exportConfig: ExportConfig | undefined;
}): ReactElement {
  const navigate = useNavigate();
  const getErrorMessage = useErrorMessageFormatter();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [createExportConfig] = useMutation<
    CreateS3TenantExportConfigMutation,
    CreateS3TenantExportConfigMutationVariables
  >(CREATE_S3_TENANT_EXPORT_CONFIG);

  async function handleSubmitCreate(settings: CreateS3TenantExportConfigInput) {
    const { errors } = await createExportConfig({
      variables: { input: settings },
    });

    if (errors) {
      throw new Error(getErrorMessage(errors));
    } else {
      setDialogOpen(false);
    }
  }

  const [updateExportConfig] = useMutation<
    UpdateS3TenantExportConfigMutation,
    UpdateS3TenantExportConfigMutationVariables
  >(UPDATE_S3_TENANT_EXPORT_CONFIG);

  async function handleSubmitUpdate(settings: UpdateS3TenantExportConfigInput) {
    const { errors } = await updateExportConfig({
      variables: { input: settings },
    });

    if (errors) {
      throw new Error(getErrorMessage(errors));
    } else {
      setDialogOpen(false);
    }
  }

  const [deleteExportConfig] = useMutation<
    DeleteS3TenantExportConfigMutation,
    DeleteS3TenantExportConfigMutationVariables
  >(DELETE_S3_TENANT_EXPORT_CONFIG, {
    onErrorSnackbarMessage: "Failed to disable S3 export",
  });

  function renderStatus() {
    if (!exportConfig) {
      return "Not configured";
    }

    if (!exportConfig.lastRunJob) {
      return "Has not run yet";
    }

    const startedAt = new Date(
      exportConfig.lastRunJob.startedAt
    ).toLocaleString();

    switch (exportConfig.lastRunJob.status) {
      case TrackedJobStatus.Created:
      case TrackedJobStatus.Queued:
      case TrackedJobStatus.Running:
        return `Running now (started at ${startedAt})`;
      case TrackedJobStatus.Done:
        return `Last run succeeded (started at ${startedAt})`;
      case TrackedJobStatus.Failed:
        return `Last run failed (started at ${startedAt}): ${exportConfig.lastRunJob.errorMessage}`;
      case TrackedJobStatus.Stalled:
        return `Last run failed (started at ${startedAt}): job stalled`;
    }
  }

  return (
    <Card>
      <CardHeader title="S3 Export" />
      <CardContent>
        <p>
          S3 Exports allow you to export all of your data to AWS S3. From there,
          you can query the data directly using Amazon Athena, or load it into
          Civis, Amazon Redshift, or other data warehouses. S3 Exports run
          nightly.
        </p>
        <p>
          <strong>Status:</strong> {renderStatus()}
        </p>
        <FlexBox flexDirection="row" justifyContent="space-between">
          <div>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="contained"
              color="primary"
              sx={{ marginRight: 1 }}
            >
              {exportConfig ? "Edit Configuration" : "Configure"}
            </Button>
            {exportConfig && (
              <S3ExportDeleteButton onDelete={deleteExportConfig} />
            )}
          </div>
          <div>
            <Button
              onClick={() => navigate("/organization/exports/dictionary")}
            >
              Data Dictionary
            </Button>
          </div>
        </FlexBox>

        {dialogOpen && (
          <S3ExportConfigDialog
            onClose={() => setDialogOpen(false)}
            exportConfig={exportConfig}
            handleSubmitCreate={handleSubmitCreate}
            handleSubmitUpdate={handleSubmitUpdate}
          />
        )}
      </CardContent>
    </Card>
  );
}
